/**
 * Onboarding — Lovable primitive adapter.
 *
 * The `features/onboarding` module is the authoritative state container
 * (ADR-108 §6 — draft + hook + provision mutation). The `shared/ui/onboarding`
 * Lovable pack is a dumb presentation layer with its own structural type set.
 * Shape overlap is high but not 100%:
 *
 *   • `SubVerticalOption.defaultOperatingHours`
 *       features  → { weekday: 0..6, openTime, closeTime }  (null = closed)
 *       lovable   → { weekday: 0..6, open, close, closed?   }
 *
 *   • `BranchStepDraft.operatingHours` same mismatch as above.
 *
 * This file translates between them without leaking adapter state back into
 * the feature domain.
 */
import type {
  OperatingHoursEntry as FeatureOperatingHoursEntry,
  SubVerticalOption as FeatureSubVerticalOption,
  BranchStepDraft as FeatureBranchStepDraft,
  OnboardingDraft as FeatureOnboardingDraft,
} from './types';
import type {
  OperatingHoursEntry as LovableOperatingHoursEntry,
  SubVerticalOption as LovableSubVerticalOption,
  OnboardingDraft as LovableOnboardingDraft,
} from '@/shared/ui/onboarding/types';

function toLovableHours(
  entries: FeatureOperatingHoursEntry[],
): LovableOperatingHoursEntry[] {
  return entries.map(entry => {
    const closed = entry.openTime == null || entry.closeTime == null;
    return {
      weekday: entry.weekday,
      open: entry.openTime ?? '00:00',
      close: entry.closeTime ?? '00:00',
      closed,
    };
  });
}

export function toLovableSubVerticalOption(
  option: FeatureSubVerticalOption,
): LovableSubVerticalOption {
  return {
    key: option.key,
    category: option.category,
    displayName: option.displayName,
    description: option.description,
    engineKind: option.engineKind,
    modules: option.modules,
    defaultOperatingHours: toLovableHours(option.defaultOperatingHours),
    complianceHints: option.complianceHints,
  };
}

export function toLovableSubVerticalOptions(
  options: readonly FeatureSubVerticalOption[],
): LovableSubVerticalOption[] {
  return options.map(toLovableSubVerticalOption);
}

export function toLovableDraft(draft: FeatureOnboardingDraft): LovableOnboardingDraft {
  return {
    version: 1,
    updatedAt: new Date().toISOString(),
    account: draft.account,
    business: draft.business,
    branch: {
      branchName: draft.branch.branchName,
      address: draft.branch.address,
      timezone: draft.branch.timezone,
      phone: draft.branch.phone,
      operatingHours: draft.branch.operatingHours
        ? toLovableHours(draft.branch.operatingHours)
        : undefined,
    },
  };
}

/**
 * Branch patch goes in Lovable shape, back to feature shape for the hook.
 */
export function fromLovableBranchPatch(
  patch: Partial<LovableOnboardingDraft['branch']>,
): Partial<FeatureBranchStepDraft> {
  const { operatingHours, ...rest } = patch;
  if (!operatingHours) return rest;
  return {
    ...rest,
    operatingHours: operatingHours.map(entry =>
      entry.closed
        ? { weekday: entry.weekday, openTime: null, closeTime: null }
        : { weekday: entry.weekday, openTime: entry.open, closeTime: entry.close },
    ),
  };
}
